import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Fab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  DragIndicator,
  AccessTime,
  Settings
} from '@mui/icons-material';
import moment from 'moment';
import { getSystemSetting, setSystemSetting } from '../../services/systemSettingsService';

const DEFAULT_TIME_SLOTS = [
  '07:00-10:00',
  '10:00-13:00',
  '11:00-14:00',
  '14:00-17:00',
  '16:00-19:00',
  '19:00-22:00',
  '20:00-23:00'
];

const DAYS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

const AdminDragDropGrid = ({
  schedules = [],
  users = [],
  weekDates = [],
  onEdit,
  onDelete,
  onAdd,
  onMove
}) => {
  const [timeSlots, setTimeSlots] = useState(DEFAULT_TIME_SLOTS);
  const [draggedSchedule, setDraggedSchedule] = useState(null);
  const [quickAddDialog, setQuickAddDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [quickForm, setQuickForm] = useState({
    userId: '',
    platform: 'shopee',
    startTime: '',
    endTime: '',
    notes: ''
  });
  
  // Time slot management
  const [timeSlotDialog, setTimeSlotDialog] = useState(false);
  const [timeSlotForm, setTimeSlotForm] = useState({
    startTime: '07:00',
    endTime: '10:00'
  });
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  
  // Load custom time slots from database on mount
  useEffect(() => {
    const loadTimeSlots = async () => {
      setLoadingTimeSlots(true);
      try {
        const result = await getSystemSetting('customTimeSlots');
        if (result.success && result.data) {
          setTimeSlots(result.data);
          // Also cache in localStorage for offline access
          localStorage.setItem('adminCustomTimeSlots', JSON.stringify(result.data));
        } else {
          // Fallback to localStorage if API fails
          const saved = localStorage.getItem('adminCustomTimeSlots');
          if (saved) {
            setTimeSlots(JSON.parse(saved));
          }
        }
      } catch (error) {
        console.error('Error loading time slots:', error);
        // Fallback to localStorage
        const saved = localStorage.getItem('adminCustomTimeSlots');
        if (saved) {
          setTimeSlots(JSON.parse(saved));
        }
      } finally {
        setLoadingTimeSlots(false);
      }
    };
    
    loadTimeSlots();
  }, []);
  
  // Save time slots to database whenever they change
  const saveTimeSlotsToDatabase = async (newTimeSlots) => {
    try {
      const result = await setSystemSetting(
        'customTimeSlots', 
        newTimeSlots,
        'Custom time slots for admin drag-drop grid'
      );
      
      if (result.success) {
        // Also save to localStorage as cache
        localStorage.setItem('adminCustomTimeSlots', JSON.stringify(newTimeSlots));
        console.log('✅ Time slots saved to database');
      } else {
        console.error('Failed to save time slots:', result.error);
      }
    } catch (error) {
      console.error('Error saving time slots:', error);
    }
  };
  
  // Get schedules for specific day and time slot
  const getSchedulesForSlot = (dayIndex, timeSlot) => {
    if (!weekDates[dayIndex]) return [];
    
    const date = moment(weekDates[dayIndex]).format('YYYY-MM-DD');
    const [startTime, endTime] = timeSlot.split('-');
    
    return schedules.filter(schedule => {
      const scheduleDate = moment(schedule.date).format('YYYY-MM-DD');
      return scheduleDate === date &&
             schedule.startTime === startTime &&
             schedule.endTime === endTime;
    });
  };
  
  // Handle drag start
  const handleDragStart = (e, schedule) => {
    setDraggedSchedule(schedule);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  // Handle drop
  const handleDrop = (e, dayIndex, timeSlot) => {
    e.preventDefault();
    
    if (!draggedSchedule) return;
    
    const [startTime, endTime] = timeSlot.split('-');
    const newDate = moment(weekDates[dayIndex]).format('YYYY-MM-DD');
    
    // Check if moving to same slot
    if (moment(draggedSchedule.date).format('YYYY-MM-DD') === newDate &&
        draggedSchedule.startTime === startTime) {
      setDraggedSchedule(null);
      return;
    }
    
    // Call move handler
    if (onMove) {
      onMove(draggedSchedule._id, {
        date: newDate,
        startTime,
        endTime
      });
    }
    
    setDraggedSchedule(null);
  };
  
  // Handle quick add click
  const handleQuickAddClick = (dayIndex, timeSlot) => {
    const [startTime, endTime] = timeSlot.split('-');
    setSelectedSlot({
      dayIndex,
      date: moment(weekDates[dayIndex]).format('YYYY-MM-DD'),
      startTime,
      endTime
    });
    setQuickForm({
      userId: '',
      platform: 'shopee',
      startTime,
      endTime,
      notes: ''
    });
    setQuickAddDialog(true);
  };
  
  // Handle quick add submit
  const handleQuickAddSubmit = () => {
    if (!quickForm.userId || !selectedSlot) {
      alert('Vui lòng chọn nhân viên');
      return;
    }
    
    if (onAdd) {
      onAdd({
        userId: quickForm.userId,
        platform: quickForm.platform,
        date: selectedSlot.date,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        notes: quickForm.notes,
        status: 'approved' // Admin tạo = approve luôn
      });
    }
    
    setQuickAddDialog(false);
    setSelectedSlot(null);
  };
  
  // Get platform color
  const getPlatformColor = (platform) => {
    return platform === 'shopee' ? '#EE4D2D' : '#000000';
  };
  
  // Handle add new time slot
  const handleAddTimeSlot = async () => {
    const { startTime, endTime } = timeSlotForm;
    
    if (!startTime || !endTime) {
      alert('Vui lòng nhập đầy đủ giờ bắt đầu và kết thúc');
      return;
    }
    
    if (startTime >= endTime) {
      alert('Giờ bắt đầu phải nhỏ hơn giờ kết thúc');
      return;
    }
    
    const newSlot = `${startTime}-${endTime}`;
    
    if (timeSlots.includes(newSlot)) {
      alert('Khung giờ này đã tồn tại');
      return;
    }
    
    // Add and sort time slots
    const updatedSlots = [...timeSlots, newSlot].sort();
    setTimeSlots(updatedSlots);
    
    // Save to database
    await saveTimeSlotsToDatabase(updatedSlots);
    
    setTimeSlotDialog(false);
    setTimeSlotForm({ startTime: '07:00', endTime: '10:00' });
  };
  
  // Handle delete time slot
  const handleDeleteTimeSlot = async (slot) => {
    if (window.confirm(`Bạn có chắc muốn xóa khung giờ ${slot}?`)) {
      const updatedSlots = timeSlots.filter(s => s !== slot);
      setTimeSlots(updatedSlots);
      // Save to database
      await saveTimeSlotsToDatabase(updatedSlots);
    }
  };
  
  // Handle reset to default
  const handleResetTimeSlots = async () => {
    if (window.confirm('Khôi phục về khung giờ mặc định?')) {
      setTimeSlots(DEFAULT_TIME_SLOTS);
      // Save to database
      await saveTimeSlotsToDatabase(DEFAULT_TIME_SLOTS);
    }
  };
  
  return (
    <Box sx={{ position: 'relative' }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        💡 <strong>Hướng dẫn:</strong>
        <br />
        • <strong>Kéo thả</strong>: Click giữ icon ⋮⋮ và kéo sang ô khác để đổi lịch
        <br />
        • <strong>Thêm nhanh</strong>: Click nút ➕ ở ô trống để thêm lịch mới
        <br />
        • <strong>Sửa/Xóa</strong>: Click icon ✏️ hoặc 🗑️ trên mỗi lịch
        <br />
        • <strong>Tùy chỉnh khung giờ</strong>: Click nút ⚙️ góc dưới bên phải ({timeSlots.length} khung giờ)
      </Alert>
      
      {/* Floating button to manage time slots */}
      <Tooltip title="Quản lý khung giờ">
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000
          }}
          onClick={() => setTimeSlotDialog(true)}
        >
          <Settings />
        </Fab>
      </Tooltip>
      
      <Paper elevation={3} sx={{ overflow: 'auto' }}>
        <Box sx={{ minWidth: 1000 }}>
          {/* Header */}
          <Grid container sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <Grid item xs={1.5}>
              <Box sx={{ p: 1.5, fontWeight: 'bold', textAlign: 'center' }}>
                Khung giờ
              </Box>
            </Grid>
            {DAYS.map((day, index) => (
              <Grid item xs={1.5} key={day}>
                <Box sx={{ p: 1.5, fontWeight: 'bold', textAlign: 'center' }}>
                  <Typography variant="body2" fontWeight="bold">
                    {day}
                  </Typography>
                  <Typography variant="caption">
                    {weekDates[index] && moment(weekDates[index]).format('DD/MM')}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          
          {/* Grid */}
          {timeSlots.map((timeSlot, slotIndex) => (
            <Grid container key={timeSlot} sx={{ borderTop: 1, borderColor: 'divider' }}>
              {/* Time Label */}
              <Grid item xs={1.5}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRight: 1,
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    {timeSlot}
                  </Typography>
                </Box>
              </Grid>
              
              {/* Day Slots */}
              {DAYS.map((day, dayIndex) => {
                const slotSchedules = getSchedulesForSlot(dayIndex, timeSlot);
                
                return (
                  <Grid item xs={1.5} key={`${day}-${slotIndex}`}>
                    <Box
                      sx={{
                        p: 1,
                        minHeight: 100,
                        borderRight: 1,
                        borderColor: 'divider',
                        bgcolor: draggedSchedule ? 'grey.100' : 'white',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'grey.50'
                        }
                      }}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, dayIndex, timeSlot)}
                    >
                      <Stack spacing={0.5}>
                        {slotSchedules.map((schedule) => {
                          const user = users.find(u => u._id === (schedule.userId?._id || schedule.userId));
                          
                          return (
                            <Paper
                              key={schedule._id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, schedule)}
                              sx={{
                                p: 1,
                                bgcolor: getPlatformColor(schedule.platform),
                                color: 'white',
                                cursor: 'move',
                                transition: 'all 0.2s',
                                opacity: draggedSchedule?._id === schedule._id ? 0.5 : 1,
                                '&:hover': {
                                  transform: 'scale(1.02)',
                                  boxShadow: 2
                                }
                              }}
                            >
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <DragIndicator sx={{ fontSize: 16, opacity: 0.7 }} />
                                <Box flex={1}>
                                  <Typography variant="caption" display="block" fontWeight="bold">
                                    {user?.fullName || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                                    {schedule.platform === 'shopee' ? '🛒' : '🎵'}
                                  </Typography>
                                </Box>
                                <Stack direction="row" spacing={0.25}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onEdit && onEdit(schedule._id, schedule);
                                    }}
                                    sx={{ color: 'white', p: 0.25 }}
                                  >
                                    <Edit sx={{ fontSize: 14 }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDelete && onDelete(schedule._id);
                                    }}
                                    sx={{ color: 'white', p: 0.25 }}
                                  >
                                    <Delete sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Stack>
                              </Stack>
                            </Paper>
                          );
                        })}
                        
                        {/* Add Button */}
                        {slotSchedules.length === 0 && (
                          <Tooltip title="Thêm lịch nhanh">
                            <Button
                              size="small"
                              startIcon={<Add />}
                              onClick={() => handleQuickAddClick(dayIndex, timeSlot)}
                              sx={{
                                minHeight: 80,
                                border: '2px dashed',
                                borderColor: 'grey.300',
                                color: 'grey.500',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                                  bgcolor: 'primary.light'
                                }
                              }}
                            >
                              Thêm
                            </Button>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ))}
        </Box>
      </Paper>
      
      {/* Quick Add Dialog */}
      <Dialog open={quickAddDialog} onClose={() => setQuickAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Thêm lịch nhanh
          {selectedSlot && (
            <Typography variant="caption" display="block" color="text.secondary">
              {moment(selectedSlot.date).format('DD/MM/YYYY')} • {selectedSlot.startTime}-{selectedSlot.endTime}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Nhân viên *</InputLabel>
              <Select
                value={quickForm.userId}
                onChange={(e) => setQuickForm({ ...quickForm, userId: e.target.value })}
                label="Nhân viên *"
              >
                {users.map(user => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.fullName} ({user.username})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Platform</InputLabel>
              <Select
                value={quickForm.platform}
                onChange={(e) => setQuickForm({ ...quickForm, platform: e.target.value })}
                label="Platform"
              >
                <MenuItem value="shopee">🛒 Shopee</MenuItem>
                <MenuItem value="tiktok">🎵 TikTok</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Ghi chú"
              value={quickForm.notes}
              onChange={(e) => setQuickForm({ ...quickForm, notes: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            
            <Alert severity="success">
              Lịch sẽ được tạo với trạng thái <strong>Đã duyệt</strong>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickAddDialog(false)}>Hủy</Button>
          <Button onClick={handleQuickAddSubmit} variant="contained">
            Thêm ngay
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Time Slot Management Dialog */}
      <Dialog 
        open={timeSlotDialog} 
        onClose={() => setTimeSlotDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTime />
            Quản lý khung giờ live
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Lưu ý:</strong> Các khung giờ này sẽ hiển thị trên lưới kéo thả. 
              Bạn có thể thêm khung giờ tùy chỉnh hoặc xóa khung giờ không dùng.
            </Alert>
            
            {/* Add new time slot form */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                ➕ Thêm khung giờ mới
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label="Giờ bắt đầu"
                  type="time"
                  value={timeSlotForm.startTime}
                  onChange={(e) => setTimeSlotForm({ ...timeSlotForm, startTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <Typography>→</Typography>
                <TextField
                  label="Giờ kết thúc"
                  type="time"
                  value={timeSlotForm.endTime}
                  onChange={(e) => setTimeSlotForm({ ...timeSlotForm, endTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <Button 
                  variant="contained" 
                  startIcon={<Add />}
                  onClick={handleAddTimeSlot}
                >
                  Thêm
                </Button>
              </Stack>
            </Paper>
            
            {/* Current time slots list */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              📋 Danh sách khung giờ ({timeSlots.length})
            </Typography>
            <List sx={{ bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
              {timeSlots.map((slot, index) => (
                <ListItem 
                  key={slot}
                  sx={{ 
                    '&:hover': { bgcolor: 'action.hover' },
                    borderBottom: index < timeSlots.length - 1 ? 1 : 0,
                    borderColor: 'divider'
                  }}
                >
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTime fontSize="small" color="action" />
                        <Typography variant="body1" fontWeight="medium">
                          {slot}
                        </Typography>
                        {DEFAULT_TIME_SLOTS.includes(slot) && (
                          <Chip label="Mặc định" size="small" color="default" />
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Xóa khung giờ">
                      <IconButton 
                        edge="end" 
                        onClick={() => handleDeleteTimeSlot(slot)}
                        color="error"
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={handleResetTimeSlots} 
            startIcon={<Delete />}
            color="warning"
          >
            Khôi phục mặc định
          </Button>
          <Box flex={1} />
          <Button onClick={() => setTimeSlotDialog(false)}>
            Đóng
          </Button>
          <Button 
            onClick={() => setTimeSlotDialog(false)} 
            variant="contained"
          >
            Lưu & Đóng
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDragDropGrid;

